import Phaser from 'phaser';
import {Painter} from './Painter';
import { ITheme, Theme} from './Theme';

/**
 * 控件对齐选项
 */
export interface AnchorOption{
    top?:number;
    bottom?:number;
    left?:number;
    right?:number;
    centerX?:number;
    centerY?:number;
}


/**
 * 控件基类（实现定位、大小、样式等基础属性）
 * 默认基于 (0, 0) 定位
 */
export class Control extends Phaser.GameObjects.Container
    implements ITheme
{
    protected graphics : Phaser.GameObjects.Graphics;
    private isShowBounds: boolean = false;
    private anchor: AnchorOption = {};
    private _x: number;
    private _y: number;


    constructor(scene: Phaser.Scene, x: number = 0, y: number = 0, width: number = 0, height: number = 0) {
        // 有没有办法自动获取scene，而不用传递这个scene参数？

        super(scene, x, y);
        this._x = x;
        this._y = y;
        this.graphics = this.scene.add.graphics();
        this.add(this.graphics);

        this.setSize(width, height);
        scene.add.existing(this);

        // 监听窗口大小变化
        //scene.scale.on('resize', this.updateLayout(), this);  // 暂时不监听窗口大小变化，怕会有重复更新问题
    }

    //------------------------------------------------
    // Theme
    //------------------------------------------------
    // theme
    protected get theme() : Theme { 
        return  Theme.current
    };
    public setTheme(theme: Theme): void {
        Theme.current = theme;
        this.draw();
    }


    //------------------------------------------------
    // Theme color
    //------------------------------------------------
    // themeCls
    protected themeCls: string = 'default';
    public setThemeClass(themeCls: string): void {
        this.themeCls = themeCls;
        this.draw();
    }
    protected get themeColor(): number {
        return this.getColor(this.themeCls);
    }

    /**
     * Get color from theme by class name.
     * @param themeCls Theme class name.
     * @returns Color.
     */ 
    public getColor(themeCls: string): number {
        switch (themeCls){
            case "bg":        return this.theme.color.bg;          break;
            case "primary":   return this.theme.color.primary;     break;
            case "secondary": return this.theme.color.secondary;   break;
            case "success":   return this.theme.color.success;     break;
            case "info":      return this.theme.color.info;        break;
            case "warning":   return this.theme.color.warning;     break;
            case "danger":    return this.theme.color.danger;      break;
            default:          return this.theme.color.primary;     break;
        }
    }

    //------------------------------------------------
    // Draw
    //------------------------------------------------
    /**draw control ui */
    protected draw() {
        this.graphics.clear();
        if (this.isShowBounds){
            this.drawBounds();
        }
    }

    //------------------------------------------------
    // Bounds
    //------------------------------------------------
    /**show bounds */
    public showBounds(value: boolean) : this {
        this.isShowBounds = value;
        this.draw();
        return this;
    }

    /**draw bounds */
    protected drawBounds(options: { lineColor?: number, lineWidth?: number, dashLength?:number, gapLength?:number } = {}): void {
        const bounds = this.getBounds();
        const lineColor = options.lineColor ?? 0xa0a0a0;
        const lineWidth = options.lineWidth ?? 1;
        const dashLength = options.dashLength?? 3;
        const gapLength = options.gapLength?? 3;
        
        Painter.drawDashedRectangle(this.graphics, 0, 0, bounds.width, bounds.height, dashLength, gapLength, lineWidth, lineColor);
    }

    /**获取容器边界 */
    public getBounds(): Phaser.Geom.Rectangle {
        const bounds = super.getBounds();
        bounds.x = this.x;
        bounds.y = this.y;
        bounds.width = this.width;
        bounds.height = this.height;
        return bounds;
    }


    //------------------------------------------------
    // Layout
    //------------------------------------------------
    /**设置遮罩图形 */
    protected setMaskGrahpics(maskGraphics: Phaser.GameObjects.Graphics): this {
        this.setMask(new Phaser.Display.Masks.GeometryMask(this.scene, maskGraphics));
        return this;
    }

    /**Set control origin layout 
     * @param px Origin x, 0.0-1.0
     * @param py Origin y, 0.0-1.0
    */
    public setOrigin(px: number, py?: number): this {
        if (py === undefined) py = px;
        this.x = this._x - (this.width * px);
        this.y = this._y - (this.height * py);
        return this;
    }

    //------------------------------------------------
    // Layout anchor
    //------------------------------------------------
    /**
     * 设置控件的锚点布局
     * @param options 锚点选项
     * @param refresh 是否立即更新布局
     */
    public setAnchor(options: AnchorOption, refresh: boolean=false): this {
        this.anchor = options;
        if (refresh){
            this.updateLayout();
        }
        return this;
    }

    /** 更新自己及子元素的布局 */
    public updateLayout(): this {
        this.layoutMe();
        this.list.forEach(child => {
            if (child instanceof Control) {
                child.updateLayout();
            }
        })
        return this;
    }

    /**
     * 更新控件布局
     */
    protected layoutMe(): void {
        const parent = this.parentContainer;
        var size = this.scene.scale.gameSize;
        const parentBounds = (parent != null) 
            ? parent.getBounds() 
            : new Phaser.Geom.Rectangle(0, 0, size.width, size.height)
            ;

        let x = this._x;
        let y = this._y;

        // 处理水平方向
        if (this.anchor.left !== undefined && this.anchor.right !== undefined) {
            this.width = parentBounds.width - this.anchor.left - this.anchor.right;  // 左右都固定，调整宽度
            x = this.anchor.left;
        } else if (this.anchor.left !== undefined) {
            x = this.anchor.left;
        } else if (this.anchor.right !== undefined) {
            x = parentBounds.width - this.width - this.anchor.right;
        } else if (this.anchor.centerX !== undefined) {
            x = (parentBounds.width - this.width) / 2 + this.anchor.centerX;
        }

        // 处理垂直方向
        if (this.anchor.top !== undefined && this.anchor.bottom !== undefined) {
            this.height = parentBounds.height - this.anchor.top - this.anchor.bottom;   // 上下都固定，调整高度
            y = this.anchor.top;
        } else if (this.anchor.top !== undefined) {
            y = this.anchor.top;
        } else if (this.anchor.bottom !== undefined) {
            y = parentBounds.height - this.height - this.anchor.bottom;
        } else if (this.anchor.centerY !== undefined) {
            y = (parentBounds.height - this.height) / 2 + this.anchor.centerY;
        }

        this.x = x;
        this.y = y;
        this.setSize(this.width, this.height);
        //this.setPosition(x, y);
        this.draw();
    }

}


